/* eslint-disable no-underscore-dangle */
import type {
  AlgoliaHit,
  CommunityLearnerSearchResult,
  TopicAlgoliaHit,
  PostAlgoliaHit,
  CommentAlgoliaHit,
  AcademyContentAlgoliaHit,
  AcademyLearnerSearchResult,
} from '../types';
import { APP_CONSTANTS } from '~/common/explicit-exports';

export const formatAlgoliaHitForHubResult = (
  hit: AlgoliaHit,
): CommunityLearnerSearchResult | AcademyLearnerSearchResult => {
  const lastUpdatedDateTime = new Date(hit.updated_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  // Community Content

  if (hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_TOPIC) {
    const topic = hit as TopicAlgoliaHit;
    return {
      id: topic.objectID,
      type: topic.type,
      resultTitle: topic.title,
      resultBody: topic.description,
      lastUpdated: lastUpdatedDateTime,
      topicId: topic.objectID,
      topicName: topic.title,
      highlightedResult: topic._highlightResult,
      snippetResult: topic._snippetResult,
    };
  }

  if (hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_POST) {
    const post = hit as PostAlgoliaHit;
    return {
      id: post.objectID,
      type: post.type,
      resultTitle: post.title,
      resultBody: post.content,
      lastUpdated: lastUpdatedDateTime,
      topicId: post.breadcrumbs[0][0],
      topicName: post.breadcrumbs[0][1],
      postId: post.objectID,
      postTitle: post.title,
      highlightedResult: post._highlightResult,
      snippetResult: post._snippetResult,
    };
  }

  if (hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_COMMENT) {
    const comment = hit as CommentAlgoliaHit;

    return {
      id: comment.objectID,
      type: comment.type,
      breadcrumbs: comment.breadcrumbs,
      resultTitle: comment.content,
      resultBody: null,
      lastUpdated: lastUpdatedDateTime,
      topicId: comment.breadcrumbs[0][0],
      topicName: comment.breadcrumbs[0][1],
      postId: comment.breadcrumbs[1][0],
      postTitle: comment.breadcrumbs[1][1],
      ...(comment.breadcrumbs[2]
        ? {
            commentId: comment.breadcrumbs[2][0],
            childCommentId: comment.objectID,
          }
        : {
            commentId: comment.objectID,
          }),
      highlightedResult: comment._highlightResult,
      snippetResult: comment._snippetResult,
    };
  }

  // Academy Content
  if (
    hit.type === APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION_CONTENT ||
    hit.type === APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_PATH ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_PATH_CONTENT ||
    hit.type === APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_COLLECTION ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_COLLECTION_CONTENT ||
    hit.type === APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_EVENT ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_SESSION ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CUSTOM_PAGE ||
    hit.type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CUSTOM_PAGE_OBJECT
  ) {
    const academyContent = hit as AcademyContentAlgoliaHit;

    return {
      id: academyContent.id || academyContent.objectID,
      type: academyContent.type,
      contentId: academyContent.content_id,
      contentType: academyContent.content_type,
      resultTitle: academyContent.name,
      resultBody: academyContent.description,
      parentName: academyContent.parent_name,
      parentId: academyContent.parent_id,
      parentType: academyContent.parent_type,
      lastUpdated: lastUpdatedDateTime,
      highlightedResult: academyContent._highlightResult,
      snippetResult: academyContent._snippetResult,
      slug: academyContent.slug,
    };
  }

  return {
    id: hit.objectID,
    type: hit.type,
    resultTitle: hit.title,
    resultBody: null,
    lastUpdated: lastUpdatedDateTime,
    highlightedResult: hit._highlightResult,
    snippetResult: hit._snippetResult,
  };
};
